import { JournalAPI } from '@business/api/journal';
import { CompactJournalResponse, JournalId, JournalRequest, JournalResponse } from '@business/entities/journal';
import { handleMessage, requestMessage } from '@business/messages';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { showDeleteConfirmation } from '@modules/common';
import { of } from 'rxjs';
import { switchMap } from 'rxjs/operators';

export class JournalBloc extends BaseBloc {
  private readonly journalRepo = new Repository<CompactJournalResponse | JournalResponse>({
    getItemId: journal => journal.id,
  });

  readonly journals$ = this.journalRepo.items$;
  readonly selectJournal = this.journalRepo.selectItem;

  onReset() {
    this.journalRepo.reset();
  }

  appendNewItems = (items: (CompactJournalResponse | JournalResponse)[], reset = false) => {
    return this.journals$.pipe(
      switchMap(existingItems => {
        const newItems = items.filter(item => !existingItems.some(existing => existing.id === item.id));
        if (newItems.length === 0) return of(null);
        if (reset) {
          return of(items).pipe(this.journalRepo.ops.reset());
        }
        return of(newItems).pipe(this.journalRepo.ops.upsertMany(item => ({ item })));
      }),
    );
  };

  fetchJournal = (id: JournalId) => {
    return JournalAPI.fetchJournal(id).pipe(
      this.journalRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_journal_error') }),
    );
  };

  createJournal = (journal: JournalRequest) => {
    return JournalAPI.createJournal(journal).pipe(
      this.journalRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_journal'),
      }),
    );
  };

  updateJournal = (id: JournalId, journal: JournalRequest) => {
    return JournalAPI.updateJournal(id, journal).pipe(
      this.journalRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_journal'),
      }),
    );
  };

  deleteJournal = (id: JournalId) => {
    return showDeleteConfirmation('Delete Journal', 'Do you really want to remove this journal entry?').pipe(
      switchMap(() => JournalAPI.deleteJournal(id)),
      this.journalRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_journal'),
      }),
    );
  };

  likeJournal = (id: JournalId) => {
    return JournalAPI.likeJournal(id).pipe(this.journalRepo.ops.upsertOne(item => ({ item })));
  };

  dislikeJournal = (id: JournalId) => {
    return JournalAPI.dislikeJournal(id).pipe(this.journalRepo.ops.upsertOne(item => ({ item })));
  };
}
